/**
 * mutateJson — single wrapper for every POST / PATCH / DELETE call
 * made by a viewmodel.
 *
 * Per SOP 02 §"Cross-cutting Utilities (LOCKED)". Collapses the 15-line
 * fetch + headers + JSON.stringify + ok-branch + error-parse + data-unwrap
 * boilerplate every viewmodel was repeating into one helper.
 *
 * Wire envelope (mirror of `gateway/responses.ts`):
 *   ok:  { success: true,  data: T }
 *   err: { success: false, error: { code, message, field? } }
 *
 * DELETE convention: server returns `{ ok: true }` as data; caller can
 * ignore the body and check `result.ok`. Bodies that fail to parse on
 * success are treated as `{ data: undefined }` so DELETE-with-204 also
 * resolves cleanly.
 */

import { parseApiError } from './parseApiError'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from './server-unavailable'

/**
 * Mirrors the signature of `useAuth().authedFetch` —
 * accepts a plain string path (the gateway base is prepended inside
 * the auth context). Keep it narrow on purpose so a caller can't pass
 * a URL/Request object that the auth wrapper doesn't understand.
 */
export type AuthedFetch = (
  path: string,
  init?: RequestInit
) => Promise<Response>

export type MutateResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly field: string | null; readonly message: string }

export async function mutateJson<T>(
  authedFetch: AuthedFetch,
  url: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown
): Promise<MutateResult<T>> {
  const init: RequestInit =
    body === undefined
      ? { method }
      : {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }

  // Wrap the network call so a fetch-level throw (DNS, CORS, gateway
  // unreachable, offline) becomes the same `{ ok: false }` shape every
  // caller already handles — with the generic SERVER_UNAVAILABLE_MESSAGE
  // rather than browser-internal `TypeError: Failed to fetch` or any
  // infrastructure detail.
  let res: Response
  try {
    res = await authedFetch(url, init)
  } catch (err) {
    if (isNetworkError(err)) {
      return { ok: false, field: null, message: SERVER_UNAVAILABLE_MESSAGE }
    }
    throw err
  }
  if (!res.ok) {
    const e = await parseApiError(res)
    return { ok: false, field: e.field, message: e.message }
  }

  // Some endpoints (esp. DELETE 204) return no body. Treat parse
  // failures on success responses as "no data" rather than an error.
  let data: T
  try {
    const parsed = (await res.json()) as { success: true; data: T }
    data = parsed.data
  } catch {
    data = undefined as unknown as T
  }
  return { ok: true, data }
}
